// react
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

// api
import { energyAnalysisOpenApi as co2eApi } from '@/api/generated/energyAnalysis/endpoints/co2e';
import { energyAnalysisOpenApi as energyDataApi } from '@/api/generated/energyAnalysis/endpoints/energyData';
import type { TimeSeriesDataPoint } from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysis';
import type { GetEnergyGroupDto } from '@/api/generated/recalc/endpoints/v2/energyGroups';

// relative
import {
  calculateOverviewTrendTotals,
  type OverviewTrendTotals,
  sumEnergySince,
} from '../utils/overviewTrend';

// external
import type { ThunkDispatch } from '@reduxjs/toolkit';
import { DateTime } from 'luxon';
import type { AnyAction } from 'redux';

const ZONE = 'Europe/Copenhagen';

const toIso = (date: DateTime): string =>
  date.toISO({ suppressMilliseconds: true }) ?? '';

interface UseDeepDiveYearOverviewOptions {
  accountId: number | undefined;
  energyGroups: GetEnergyGroupDto[] | undefined;
}

interface DeepDiveYearOverview {
  /** Last 12 months consumption in kWh (m³ for water groups). */
  consumption: number | null;
  /** Last 12 months CO2e in kg. */
  co2eKg: number | null;
  trend: OverviewTrendTotals | null;
  series: TimeSeriesDataPoint[];
}

export type DeepDiveAnalysisKind = 'power' | 'heat' | 'water';

export interface DeepDiveKindTotals {
  /** Last 12 months consumption (kWh, m³ for water). */
  consumption: number | null;
  /** Last 12 months CO2e in kg. */
  co2eKg: number | null;
  series: TimeSeriesDataPoint[];
}

interface UseDeepDiveYearOverviewResult {
  /** Last 12 months consumption per energy group (kWh, m³ for water). */
  consumptionByGroupId: Record<number, number | null>;
  /** CO2e in kg per energy group for the last 12 months. */
  co2eKgByGroupId: Record<number, number | null>;
  totalsByKind: Record<DeepDiveAnalysisKind, DeepDiveKindTotals>;
  isLoading: boolean;
}

interface Target {
  energyGroupId: number;
  kind: DeepDiveAnalysisKind;
  isHeatOrWater: boolean;
}

const KINDS: DeepDiveAnalysisKind[] = ['power', 'heat', 'water'];

export const getDeepDiveAnalysisKind = (
  type: string | null | undefined,
): DeepDiveAnalysisKind =>
  type?.includes('Water') ? 'water' : type?.includes('Heat') ? 'heat' : 'power';

const EMPTY_OVERVIEW: DeepDiveYearOverview = {
  consumption: null,
  co2eKg: null,
  trend: null,
  series: [],
};

const sumSeries = (
  seriesList: TimeSeriesDataPoint[][],
): TimeSeriesDataPoint[] => {
  const byTimestamp = new Map<string, number>();
  seriesList.flat().forEach(({ timestamp, value }) => {
    byTimestamp.set(timestamp, (byTimestamp.get(timestamp) ?? 0) + value);
  });
  return [...byTimestamp].map(([timestamp, value]) => ({ timestamp, value }));
};

const sumOrNull = (values: (number | null)[]): number | null => {
  const numbers = values.filter((value): value is number => value != null);
  return numbers.length ? numbers.reduce((sum, value) => sum + value, 0) : null;
};

export const useDeepDiveYearOverview = ({
  accountId,
  energyGroups,
}: UseDeepDiveYearOverviewOptions): UseDeepDiveYearOverviewResult => {
  const dispatch = useDispatch() as ThunkDispatch<unknown, unknown, AnyAction>;
  const [loaded, setLoaded] = useState<{
    key: string;
    overviews: Record<number, DeepDiveYearOverview>;
  }>({ key: '', overviews: {} });

  const targets = useMemo(
    () =>
      (energyGroups ?? []).flatMap((group): Target[] => {
        if (
          typeof group.energyGroupId !== 'number' ||
          group.options?.state === 'Error'
        ) {
          return [];
        }

        const kind = getDeepDiveAnalysisKind(group.type);
        return [
          {
            energyGroupId: group.energyGroupId,
            kind,
            isHeatOrWater: kind !== 'power',
          },
        ];
      }),
    [energyGroups],
  );

  const requestKey =
    accountId && targets.length
      ? `${accountId}:${targets.map((target) => target.energyGroupId).join(',')}`
      : '';

  useEffect(() => {
    if (!accountId || !targets.length) return;

    let isMounted = true;
    const subscriptions: { unsubscribe?: () => void }[] = [];

    const now = DateTime.now().startOf('day').setZone(ZONE);
    const periodStart = now.minus({ months: 12 });
    const requestStart = periodStart;

    const loadGroup = async ({
      energyGroupId,
      isHeatOrWater,
    }: Target): Promise<readonly [number, DeepDiveYearOverview]> => {
      const overviewRequest = dispatch(
        energyDataApi.endpoints.energyOverviewEnergyOverviewAccountIdEnergyGroupIdGet.initiate(
          {
            accountId,
            energyGroupId,
            startTimestamp: toIso(requestStart),
            endTimestamp: toIso(now),
          },
        ),
      );
      subscriptions.push(overviewRequest);

      let overview;
      try {
        overview = await overviewRequest.unwrap();
      } catch {
        return [energyGroupId, EMPTY_OVERVIEW] as const;
      }

      // CO2e has its own data window, distinct from the energy overview's.
      const summaryRequest = dispatch(
        isHeatOrWater
          ? co2eApi.endpoints.co2ETimestampsSummaryHeatWaterCo2EEmissionTimestampsSummaryHeatWaterAccountIdEnergyGroupIdGet.initiate(
              { accountId, energyGroupId },
            )
          : co2eApi.endpoints.co2ETimestampsSummaryCo2EEmissionTimestampsSummaryAccountIdEnergyGroupIdGet.initiate(
              { accountId, energyGroupId },
            ),
      );
      subscriptions.push(summaryRequest);

      let summary;
      try {
        summary = await summaryRequest.unwrap();
      } catch {
        summary = null;
      }

      const earliest = DateTime.fromISO(
        summary?.maxEarliestDataTimestamp ?? '',
      ).setZone(ZONE);
      const latest = DateTime.fromISO(
        summary?.minLatestDataTimestamp ?? '',
      ).setZone(ZONE);
      const co2eStart = earliest.isValid
        ? DateTime.max(periodStart, earliest)
        : periodStart;
      const co2eEnd = latest.isValid ? DateTime.min(now, latest) : now;

      let co2eKg: number | null = null;
      if (co2eStart < co2eEnd) {
        const args = {
          accountId,
          energyGroupId,
          startTimestamp: toIso(co2eStart),
          endTimestamp: toIso(co2eEnd),
        };
        const co2eRequest = isHeatOrWater
          ? dispatch(
              co2eApi.endpoints.co2EOverviewHeatWaterCo2EEmissionDataHeatWaterAccountIdEnergyGroupIdGet.initiate(
                args,
              ),
            )
          : dispatch(
              co2eApi.endpoints.co2EOverviewCo2EEmissionDataAccountIdEnergyGroupIdGet.initiate(
                args,
              ),
            );
        subscriptions.push(co2eRequest);

        try {
          const co2e = await co2eRequest.unwrap();
          // The API reports grams; the ESG page converts to kg the same way.
          const grams =
            'Location' in co2e
              ? co2e.Location?.CO2eEmissionsSummary?.CO2eEmissions
              : co2e.CO2eEmissionsSummary?.CO2eEmissions;
          co2eKg = typeof grams === 'number' ? grams / 1000 : null;
        } catch {
          co2eKg = null;
        }
      }

      return [
        energyGroupId,
        {
          consumption: sumEnergySince(overview.EnergyData, periodStart),
          co2eKg,
          trend: calculateOverviewTrendTotals(overview.EnergyData),
          series: overview.EnergyData.flatMap(({ timestamp, value }) =>
            value != null ? [{ timestamp, value }] : [],
          ),
        },
      ] as const;
    };

    void Promise.all(targets.map(loadGroup)).then((entries) => {
      if (!isMounted) return;
      setLoaded({ key: requestKey, overviews: Object.fromEntries(entries) });
    });

    return () => {
      isMounted = false;
      subscriptions.forEach((subscription) => subscription.unsubscribe?.());
    };
  }, [accountId, dispatch, requestKey, targets]);

  const overviewByGroupId = loaded.key === requestKey ? loaded.overviews : {};
  const isLoading = Boolean(requestKey) && loaded.key !== requestKey;

  const totalsByKind = Object.fromEntries(
    KINDS.map((kind) => {
      const overviews = targets
        .filter((target) => target.kind === kind)
        .map((target) => overviewByGroupId[target.energyGroupId])
        .filter((overview) => overview != null);

      return [
        kind,
        {
          consumption: sumOrNull(
            overviews.map((overview) => overview.consumption),
          ),
          co2eKg: sumOrNull(overviews.map((overview) => overview.co2eKg)),
          series: sumSeries(overviews.map((overview) => overview.series)),
        },
      ];
    }),
  ) as Record<DeepDiveAnalysisKind, DeepDiveKindTotals>;

  return {
    consumptionByGroupId: Object.fromEntries(
      Object.entries(overviewByGroupId).map(([groupId, overview]) => [
        groupId,
        overview.consumption,
      ]),
    ),
    co2eKgByGroupId: Object.fromEntries(
      Object.entries(overviewByGroupId).map(([groupId, overview]) => [
        groupId,
        overview.co2eKg,
      ]),
    ),
    totalsByKind,
    isLoading,
  };
};
